import { DAYS_OF_WEEK } from '@src/constants/commons';

export type DaysOfWeekLabelVariant = 'short' | 'label';

/** Legacy workShift.daysOfWeek may store Sunday as 0 (Date.getDay / WEEKS_VALUE_TRANSLATED). */
export const LEGACY_SUNDAY_DAY_INDEX = 0;

/** Luxon isoWeekday / workShift.daysOfWeek: 1=Mon … 7=Sun. Also accepts 0 as Sunday. */
export const normalizeDaysOfWeekValue = (day: unknown): number | null => {
  const dayNumber = Number(day);
  if (Number.isNaN(dayNumber)) {
    return null;
  }

  if (dayNumber === 0) {
    return 7;
  }

  if (dayNumber >= 1 && dayNumber <= 7) {
    return dayNumber;
  }

  return null;
};

const getDayOrder = (dayValue: number): number =>
  DAYS_OF_WEEK.find(entry => entry.value === dayValue)?.order ?? dayValue;

export const normalizeDaysOfWeek = (days: unknown): number[] => {
  if (!Array.isArray(days) || days.length === 0) {
    return [];
  }

  const uniqueDays = new Set<number>();

  days.forEach(day => {
    const normalizedDay = normalizeDaysOfWeekValue(day);
    if (normalizedDay !== null) {
      uniqueDays.add(normalizedDay);
    }
  });

  return Array.from(uniqueDays).sort((left, right) => getDayOrder(left) - getDayOrder(right));
};

export const formatDaysOfWeek = (
  days: unknown,
  {
    variant = 'short',
    separator = ', ',
  }: { variant?: DaysOfWeekLabelVariant; separator?: string } = {},
): string => {
  const normalizedDays = normalizeDaysOfWeek(days);
  if (!normalizedDays.length) {
    return '';
  }

  const labelField = variant === 'label' ? 'label' : 'short';

  return normalizedDays
    .map(dayValue => DAYS_OF_WEEK.find(entry => entry.value === dayValue)?.[labelField] ?? String(dayValue))
    .join(separator);
};

/** Include legacy Sunday (0) when querying ISO Sunday (7) in Firestore. */
export const expandDaysOfWeekForFirestoreQuery = (days: unknown): number[] => {
  const expanded = new Set<number>();

  normalizeDaysOfWeek(days).forEach(day => {
    expanded.add(day);
    if (day === 7) {
      expanded.add(LEGACY_SUNDAY_DAY_INDEX);
    }
  });

  return Array.from(expanded);
};
